import React, { useState, useEffect, useCallback } from "react";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { toast } from "react-toastify";

import { openApi } from "../../services/api";

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const maxISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 60); // MAX_DAYS_AHEAD do backend
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

/**
 * Modal de agendamento da vitrine.
 *
 * openApi, nao api: a vitrine e publica e o cliente final nao tem sessao.
 * Usar "api" mandaria um Authorization vazio e o interceptor tentaria
 * renovar o token -> redireciona para o login.
 */
const AgendarModal = ({ open, onClose, slug, service, storeColor }) => {
  const cor = storeColor || "#1B7A5A";

  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [privacyLink, setPrivacyLink] = useState("");

  // Um profissional so: nao faz o cliente "escolher" sem opcao.
  useEffect(() => {
    if (!open) return;
    if (service?.staff?.length === 1) setStaffId(service.staff[0].id);
  }, [open, service]);

  useEffect(() => {
    if (!open) return;
    openApi
      .request({ url: "/public-privacy-link", method: "GET" })
      // O campo e "lgpdLink" -- conferido na resposta real do endpoint.
      // Hoje vem vazio (o usuario ainda nao configurou a URL): o aviso
      // aparece sem link, que e o certo, e nao um link quebrado.
      .then(({ data }) => setPrivacyLink(data?.lgpdLink || ""))
      .catch(() => setPrivacyLink(""));
  }, [open]);

  const carregarSlots = useCallback(() => {
    if (!staffId || !date || !service?.id) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    openApi
      .request({
        url: `/store/${slug}/slots`,
        method: "GET",
        params: { productId: service.id, staffMemberId: staffId, date },
      })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [slug, service, staffId, date]);

  useEffect(() => {
    carregarSlots();
    setTime("");
  }, [carregarSlots]);

  const fechar = () => {
    if (saving) return;
    setStaffId(service?.staff?.length === 1 ? service.staff[0].id : "");
    setDate("");
    setSlots([]);
    setTime("");
    setName("");
    setPhone("");
    setNotes("");
    onClose();
  };

  const podeConfirmar =
    !!staffId && !!date && !!time && name.trim().length >= 2 && phone.trim().length >= 10;

  const confirmar = () => {
    if (!podeConfirmar || saving) return;
    setSaving(true);

    openApi
      .request({
        url: `/store/${slug}/appointments`,
        method: "POST",
        data: {
          productId: service.id,
          staffMemberId: staffId,
          date,
          time,
          customerName: name,
          customerPhone: phone,
          notes,
        },
      })
      .then(({ data }) => {
        toast.success("Horário reservado!");
        if (data.whatsappNumber) {
          const numero = String(data.whatsappNumber).replace(/\D/g, "");
          window.open(
            `https://wa.me/${numero}?text=${encodeURIComponent(data.whatsappText)}`,
            "_blank"
          );
        }
        fechar();
      })
      .catch((err) => {
        const code = err?.response?.data?.error;
        if (code === "ERR_SLOT_TAKEN") {
          // Perdeu a corrida. Mostrar a lista ATUALIZADA, nao so um erro:
          // o horario que ele escolheu nao existe mais.
          toast.error("Esse horário acabou de ser tomado. Escolha outro.");
          setTime("");
          carregarSlots();
        } else if (code === "ERR_TOO_MANY_BOOKINGS_PHONE") {
          toast.error("Você já tem agendamentos demais para este dia.");
        } else if (code === "ERR_TOO_MANY_BOOKINGS_IP") {
          toast.error("Muitas tentativas. Aguarde alguns minutos.");
        } else if (code === "ERR_INVALID_PHONE") {
          toast.error("Telefone inválido.");
        } else if (code === "ERR_INVALID_NAME") {
          toast.error("Informe seu nome.");
        } else if (code === "ERR_OUT_OF_WINDOW") {
          toast.error("Escolha uma data válida.");
        } else {
          toast.error("Não foi possível agendar. Tente novamente.");
        }
      })
      .finally(() => setSaving(false));
  };

  if (!service) return null;

  return (
    <Dialog open={open} onClose={fechar} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>Agendar</Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 500 }}>
            {service.name}
            {service.durationMinutes ? ` · ${service.durationMinutes} min` : ""}
          </Typography>
        </Box>
        <IconButton onClick={fechar} size="small" disabled={saving}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {service.staff?.length > 1 && (
          <TextField
            select
            fullWidth
            size="small"
            label="Profissional"
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            sx={{ mb: 1.5 }}
          >
            {service.staff.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          fullWidth
          size="small"
          type="date"
          label="Data"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: hojeISO(), max: maxISO() }}
          sx={{ mb: 1.5 }}
        />

        {!!staffId && !!date && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 0.7 }}>
              Horários
            </Typography>

            {loadingSlots ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={22} />
              </Box>
            ) : slots.length ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                {slots.map((h) => (
                  <Chip
                    key={h}
                    label={h}
                    clickable
                    onClick={() => setTime(h)}
                    sx={{
                      fontWeight: 700,
                      bgcolor: time === h ? cor : undefined,
                      color: time === h ? "#fff" : undefined,
                      "&:hover": { bgcolor: time === h ? cor : undefined },
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                Sem horários disponíveis neste dia.
              </Typography>
            )}
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          label="Seu nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Seu WhatsApp"
          placeholder="(85) 99999-9999"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Observação (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          inputProps={{ maxLength: 500 }}
          sx={{ mb: 1.5 }}
        />

        <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 1.5 }}>
          Ao agendar, você concorda que seus dados sejam usados para contato sobre
          este atendimento.
          {privacyLink && (
            <>
              {" "}
              <a href={privacyLink} target="_blank" rel="noopener noreferrer">
                Política de privacidade
              </a>
            </>
          )}
        </Typography>

        <Button
          fullWidth
          disabled={!podeConfirmar || saving}
          onClick={confirmar}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            py: 1,
            bgcolor: cor,
            color: "#fff",
            "&:hover": { bgcolor: cor, opacity: 0.9 },
            "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
          }}
        >
          {saving ? "Reservando..." : "Confirmar agendamento"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default AgendarModal;
